import { useContext, useState } from 'react'

import clsx from 'clsx'

import { useMediaActionExecLogBulkApi } from '@/common/api/modules/mediaAction'
import { actionStatusBadgeType, actionStatusLabel } from '@/common/app/modules/media/mediaAction'
import { dateTimeStr, timeStr } from '@/common/form/date'
import { ActionExecLog, ExecLogStep } from '@/common/model/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import { IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { ExecLogModalContext } from '@/components/container/modal/automationReview/ExecLogModalProvider'

const stepIcon = (s: ExecLogStep): string => {
  if (s.status === 'running') return '●'
  if (s.status === 'ok' || s.ok === true) return '✓'
  if (s.status === 'failed' || s.ok === false) return '✗'
  return '・'
}

const stepTextColor = (s: ExecLogStep): TextColor => {
  if (s.status === 'running') return TextColor.CAUTION
  if (s.status === 'ok' || s.ok === true) return TextColor.SUCCESS
  if (s.status === 'failed' || s.ok === false) return TextColor.CRITICAL
  return TextColor.SECONDARY
}

// 1アクションの実行ログ表示（ステップタイムライン）
const ExecLogItem = ({ item }: { item: ActionExecLog }) => {
  return (
    <InsideArea color={BgColor.SECONDARY} border>
      <div className='flex flex-col gap-2'>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge size={10} badgeType={actionStatusBadgeType(item.status)}>
            {actionStatusLabel(item.status)}
          </Badge>
          <Text size={12} weight='bold'>
            #{item.action_id} {item.title}
          </Text>
        </div>
        <div className='flex flex-wrap gap-3'>
          <Text size={10} color={TextColor.SECONDARY}>
            開始: {item.started_at ? dateTimeStr(item.started_at) : '-'}
          </Text>
          {item.completed_at && (
            <Text size={10} color={TextColor.SECONDARY}>
              完了: {dateTimeStr(item.completed_at)}
            </Text>
          )}
          {item.phase && (
            <Text size={10} color={TextColor.PURPLE}>
              phase: {item.phase}
            </Text>
          )}
        </div>

        {(item.steps ?? []).length === 0 ? (
          <Text size={10} color={TextColor.SECONDARY}>
            ステップ未記録（旧形式 or まだ開始されていません）
          </Text>
        ) : (
          <div className='flex flex-col gap-1'>
            {item.steps?.map((s, i) => (
              <div
                key={i}
                className={clsx(
                  'border-secondary flex items-start gap-2 rounded border-l-2 px-2 py-1',
                  s.status === 'running' && 'bg-fill-caution-secondary'
                )}
              >
                <Text size={12} weight='bold' color={stepTextColor(s)}>
                  {stepIcon(s)}
                </Text>
                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                  <Text size={12} weight='bold'>
                    {s.label || s.name || s.step}
                    {s.status === 'running' && (
                      <span className='text-caution ml-1.5 text-[10px]'>● 実行中</span>
                    )}
                  </Text>
                  <div className='flex flex-wrap gap-2.5'>
                    {s.started_at && (
                      <Text size={10} color={TextColor.SECONDARY}>
                        開始: {dateTimeStr(s.started_at)}
                      </Text>
                    )}
                    {s.completed_at && (
                      <Text size={10} color={TextColor.SECONDARY}>
                        完了: {dateTimeStr(s.completed_at)}
                      </Text>
                    )}
                    {s.history_id && (
                      <Text size={10} color={TextColor.SECONDARY}>
                        履歴ID: #{s.history_id}
                      </Text>
                    )}
                    {typeof s.char_count === 'number' && (
                      <Text size={10} color={TextColor.SECONDARY}>
                        文字数: {s.char_count.toLocaleString()}
                      </Text>
                    )}
                    {s.title && (
                      <Text size={10} color={TextColor.SECONDARY}>
                        タイトル: {s.title}
                      </Text>
                    )}
                    {s.primary_keyword && (
                      <Text size={10} color={TextColor.SECONDARY}>
                        KW: {s.primary_keyword}
                      </Text>
                    )}
                  </div>
                  {s.error && (
                    <Text size={10} color={TextColor.CRITICAL}>
                      エラー: {s.error}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {item.error && (
          <Text size={10} color={TextColor.CRITICAL}>
            全体エラー: {item.error}
          </Text>
        )}
      </div>
    </InsideArea>
  )
}

// 実行ログモーダル（5秒ごとの自動更新つき）
const ExecLogModal = () => {
  const { modal, showing, hide } = useContext(ExecLogModalContext)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const { data, isLoading, mutate } = useMediaActionExecLogBulkApi(
    modal.showing && modal.actionIds.length > 0
      ? { site_id: modal.siteId, ids: modal.actionIds.join(',') }
      : null,
    showing && autoRefresh
  )
  const items = data?.items ?? []
  const anyRunning = items.some((it) => it.status === 'in_progress')

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text={modal.title || '実行ログ'} onClickClose={hide} />
      <ModalContent>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-wrap items-center gap-2'>
            {anyRunning && (
              <Badge size={10} badgeType={BadgeType.CAUTION}>
                ● 実行中
              </Badge>
            )}
            <div className='ml-auto flex items-center gap-2'>
              <Text size={10} color={TextColor.SECONDARY}>
                {data ? `更新: ${timeStr(new Date())}` : ''}
              </Text>
              <Button
                color={autoRefresh ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                onClick={() => setAutoRefresh((v) => !v)}
              >
                {autoRefresh ? '自動更新ON (5秒)' : '自動更新OFF'}
              </Button>
              <Button color={ButtonColor.SECONDARY} onClick={() => mutate()}>
                今すぐ更新
              </Button>
            </div>
          </div>

          {isLoading && items.length === 0 ? (
            <PageListLoading small />
          ) : items.length === 0 ? (
            <Text size={12} color={TextColor.SECONDARY} align='center'>
              ログがまだありません
            </Text>
          ) : (
            <div className='flex flex-col gap-2.5'>
              {items.map((it) => (
                <ExecLogItem key={it.action_id} item={it} />
              ))}
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ExecLogModal
